// @flow

/**
 * Module dependencies.
 */

import { FormGroup, Label, makeFormControl } from 'client/components/core/forms/styles';
import { branch } from 'core/utils/branch';
import { colors, media, units } from 'client/styles';
import { compose, defaultProps, mapProps, withState } from 'recompose';
import { map } from 'lodash/fp';
import { position, size } from 'polished';
import Icon from 'client/components/core/icon';
import React, { Component } from 'react';
import styled from 'styled-components';

/**
 * `Option` type.
 */

type Option = {
  label: string,
  value: number | string
};

/**
 * `Props` type.
 */

type Props = {
  defaultValue: number | string,
  hasFocus: boolean,
  hasValue: boolean,
  label: string,
  onBlur: Function,
  onFocus: Function,
  onUpdate?: Function,
  options: Array<Object>,
  placeholder? : string,
  setValueState: Function,
  themeColor?: string,
  triggerValueState: Function,
  type?: string
};

/**
 * `SelectFormControl` component.
 */

const SelectFormControl = makeFormControl('select');

/**
 * `Arrow` component.
 */

const Arrow = styled.span`
  ${position('absolute', null, '-4px', 'calc(50% - 24px)', null)};
  ${size(units(3), units(3))};

  pointer-events: none;

  ${media.min.xs`
    right: 10px;
  `}
`;

/**
 * Styles.
 */

const styles = {
  textarea: 'cursor: pointer;'
};

/**
 * `SelectComponent`.
 */

class SelectComponent extends Component {

  /**
   * Props.
   */

  props: Props;

  /**
   * Render options.
   */

  renderOptions = map((option: Option) => (
    <option
      key={option.value}
      value={option.value}
    >
      {option.label}
    </option>
  ));

  /**
   * Render placeholder,
   */

  renderPlaceholder(): React$Element<*> {
    const { placeholder } = this.props;

    return branch(placeholder,
      <option
        disabled
        hidden
        selected
      >
        {placeholder}
      </option>
    );
  }

  /**
   * Handle on change.
   */

  handleOnChange = (event: any): any => {
    const { triggerValueState, onUpdate } = this.props;

    if (onUpdate) {
      onUpdate(event);
    }

    return triggerValueState(Boolean(event.currentTarget.value));
  }

  /**
   * Render.
   */

  render(): React$Element<*> {
    const {
      defaultValue,
      hasFocus,
      hasValue,
      label,
      onBlur,
      onFocus,
      options = [],
      themeColor,
      ...rest
    } = this.props;

    const active = hasFocus || hasValue;
    const iconColor = active ? themeColor || colors.brandPrimary : colors.brandSecondary;

    return (
      <FormGroup
        hasFocus={hasFocus}
        themeColor={themeColor}
      >
        {branch(label,
          <Label
            active
            hasFocus={hasFocus}
            themeColor={themeColor}
          >
            {label}
          </Label>
        )}

        <SelectFormControl
          defaultValue={defaultValue}
          onBlur={onBlur}
          onChange={this.handleOnChange}
          onFocus={onFocus}
          styles={styles.textarea}
          themeColor={themeColor}
          {...rest}
        >
          {this.renderPlaceholder()}
          {this.renderOptions(options)}
        </SelectFormControl>

        <Arrow>
          <Icon
            color={iconColor}
            icon={'arrow-down'}
          />
        </Arrow>
      </FormGroup>
    );
  }

}

/**
 * Export `Select`.
 */

export const Select = compose(
  defaultProps({
    type: 'text'
  }),
  withState('hasFocus', 'setFocus', false),
  withState('hasValue', 'setValueState', false),
  mapProps(({ setValueState, setFocus, ...props }: Object): Props => ({
    ...props,
    onBlur: (): Function => setFocus(false),
    onFocus: (): Function => setFocus(true),
    triggerValueState: (): Function => setValueState((hasValue: boolean): boolean => !hasValue)
  })),
)(SelectComponent);



// WEBPACK FOOTER //
// ../src/client/components/core/forms/select.js